import React, { useContext } from 'react';
import { SlideDown } from 'react-slidedown';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import { NavigateToWindowWithNativeEvents } from '@components/_common/link/link';
import {
  HundredDayBoldText,
  HundredDayButton,
  HundredDayChevronIconWrapper,
  HundredDayContentButtonWrapper,
  HundredDayContentTextWrapper,
  HundredDayContentWrapper,
  HundredDayGreyText,
  HundredDayHeaderLeftSideWrapper,
  HundredDayHeaderRightSideWrapper,
  HundredDayHeaderTextWrapper,
  HundredDayHeaderWrapper,
  HundredDayLinkText,
  HundredDayRowExpiredWarrantyWrapper,
  HundredDayRowWarrantyWrapper,
  HundredDaysContentWarrantiesRow,
  HundredDayText,
  HundredDayTitle,
  HundredDayContentLeftSideWrapper,
  HundredDayContentRightSideWrapper,
  HundredDayExpiredText,
  HideText,
} from './HundredDayWarranty.styles';
import ChevronIcon from '../../../assets/ChevronIcon';
import { AnalyticEvents } from '../../../constants';

const formatRemainingMilesValue = (value: string) => {
  const tmpNumValue = value.toString().replace(/[^0-9]/g, '');
  let commaNumValue = tmpNumValue;

  if (tmpNumValue.length > 3) {
    commaNumValue = Number(tmpNumValue).toLocaleString('en-US');
  }
  return value.toString().replace(tmpNumValue, commaNumValue);
};

const Expired = () => (
  <HundredDayRowExpiredWarrantyWrapper>
    <HundredDayExpiredText data-testid="100-day-warranty-is-expired">Expired</HundredDayExpiredText>
  </HundredDayRowExpiredWarrantyWrapper>
);

const WarrantyInfo = ({ durationLeft, milesLeft }: any) => (
  <>
    <HundredDayRowWarrantyWrapper>
      <HundredDayGreyText data-testid="100-day-warranty-info-text">
        {durationLeft} | up to {milesLeft} mi<HideText>les</HideText>
      </HundredDayGreyText>
    </HundredDayRowWarrantyWrapper>
  </>
);

export const HundredDayWarrantyHeader = ({ isCardOpen, limited }: any) => {
  const { isExpired, milesLeft, durationLeft } = limited;

  let formattedDurationLeft: any = null;

  if (durationLeft) {
    formattedDurationLeft = formatRemainingMilesValue(durationLeft);
  }

  const formattedMilesLeft = formatRemainingMilesValue(milesLeft);

  let slideDownStatusClass = 'is-closed';
  if (isCardOpen) {
    slideDownStatusClass = 'is-open';
  }
  return (
    <>
      <HundredDayHeaderWrapper data-testid={slideDownStatusClass}>
        <HundredDayHeaderLeftSideWrapper>
          <HundredDayHeaderTextWrapper>
            <HundredDayTitle data-testid="100-day-warranty-title">100-Day Limited Warranty</HundredDayTitle>
            {!isCardOpen && (
              <SlideDown>
                {isExpired ? (
                  <Expired />
                ) : (
                  <WarrantyInfo durationLeft={formattedDurationLeft} milesLeft={formattedMilesLeft} />
                )}
              </SlideDown>
            )}
          </HundredDayHeaderTextWrapper>
        </HundredDayHeaderLeftSideWrapper>

        <HundredDayHeaderRightSideWrapper>
          <HundredDayChevronIconWrapper data-testid="100-day-warranty-chevron" isOpen={isCardOpen}>
            <ChevronIcon />
          </HundredDayChevronIconWrapper>
        </HundredDayHeaderRightSideWrapper>
      </HundredDayHeaderWrapper>
    </>
  );
};

export const HundredDayWarrantyContent = ({ limited, cardId, cardPosition }: any) => {
  const {
    callToAction: { title, link },
    isExpired,
    milesLeft,
    durationLeft,
    deductible,
  } = limited;
  const { trackEvent } = useContext(AnalyticsContext) as any;

  let formattedDurationLeft: any = null;

  if (durationLeft) {
    formattedDurationLeft = formatRemainingMilesValue(durationLeft);
  }

  const formattedMilesLeft = formatRemainingMilesValue(milesLeft);

  const handleRedirect = () => {
    trackEvent(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      type: [limited.type.toLowerCase()],
      isExpired: [isExpired],
    });
    datadogRum.addAction(AnalyticEvents.ovpCardClicked, {
      cardId,
      cardPosition,
      type: [limited.type.toLowerCase()],
      isExpired: [isExpired],
    });
    NavigateToWindowWithNativeEvents({
      url: link,
      event: 'link',
    });
  };

  return (
    <HundredDayContentWrapper>
      <HundredDayContentLeftSideWrapper>
        <HundredDayContentTextWrapper>
          <HundredDayText data-testid="100-day-warranty-body-paragraph">
            <SlideDown>
              <HundredDaysContentWarrantiesRow>
                {isExpired ? (
                  <Expired />
                ) : (
                  <WarrantyInfo durationLeft={formattedDurationLeft} milesLeft={formattedMilesLeft} />
                )}
              </HundredDaysContentWarrantiesRow>
            </SlideDown>
            <HundredDayBoldText>
              Your Limited Warranty offers peace of mind in the event of an unanticipated mechanical or electrical issue
              in your first 100 days or 4,189 miles with your vehicle.
            </HundredDayBoldText>
            &nbsp;The Limited Warranty does not cover cosmetic imperfections, routine maintenance or wearable items like
            brakes and tires. Please visit our &nbsp;
            <HundredDayLinkText
              href={link}
              data-testid="hundred-day-link-text"
              nativeEvent={{
                event: 'link',
                payload: { url: link },
              }}
            >
              Limited Warranty
            </HundredDayLinkText>
            &nbsp;page for more coverage information.
            <br />
            <br />
            <HundredDayBoldText>
              Our partners at preferred repair facilities are ready to help you get back on the road.&nbsp;
            </HundredDayBoldText>
            {deductible && (
              <>
                Take your vehicle to one of our preferred facilities for speedy repairs with a ${deductible?.inNetwork}{' '}
                deductible and a 12-month satisfaction guarantee. All non-preferred facility repairs will include a $
                {deductible.outOfNetwork} deductible per visit and may be subject to additional charges.
              </>
            )}
          </HundredDayText>
        </HundredDayContentTextWrapper>
      </HundredDayContentLeftSideWrapper>

      <HundredDayContentRightSideWrapper>
        <HundredDayContentButtonWrapper>
          <HundredDayButton data-testid="100-day-warranty-cta-button" size="small" onClick={handleRedirect}>
            {title}
          </HundredDayButton>
        </HundredDayContentButtonWrapper>
      </HundredDayContentRightSideWrapper>
    </HundredDayContentWrapper>
  );
};
